import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { parseIsoZonedDateTime, formatZonedDateTime } from '@portal/pkg-format/date'
import { useHasEnablement } from '@portal/pkg-authorization'
import { OverflowCell } from '@portal/pkg-ui'
import type { RoleListItemResponse } from '@portal/pkg-api/gen/admin-console'
import type { EntityType } from '@portal/pkg-api/gen/navigator'
import { entityTypeLabel } from '../model/entityTypeLabel'
import { DeleteRoleDialog } from './DeleteRoleDialog'
import { RoleForm } from './RoleForm'
import { RoleQueries } from '../queries'

const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const
const EM_DASH = '\u2014'

/** Null for a role whose row predates the legacy table stamping when it was last written. */
function LastUpdated({ value }: { value?: string | null }) {
  const { i18n } = useTranslation()
  if (!value) return <>{EM_DASH}</>
  return <>{formatZonedDateTime(parseIsoZonedDateTime(value), i18n.language)}</>
}

/**
 * The Entity Types a role is scoped to, labeled as the role form labels them. Empty until the
 * Entity Types have loaded, so an id never shows as unavailable while its name is on its way.
 */
function useRoleEntitiesLabel(
  role: RoleListItemResponse,
  entityTypes?: ReadonlyMap<number, EntityType>,
): string {
  const { t } = useTranslation('admin-console')
  if (role.entityTypeAllAccess) return t('roles.entityTypes.all')
  if (!role.entityTypeIds?.length) return EM_DASH
  if (!entityTypes) return ''
  return role.entityTypeIds.map((id) => entityTypeLabel(t, id, entityTypes)).join(', ')
}

/**
 * Delete is offered only for a role the server would in fact delete. `deletable` is false for the
 * built-in Default and Lite roles; the server refuses those with a 409 whatever the menu shows.
 */
function RoleActionMenu({
  role,
  onEdit,
  onDelete,
}: {
  role: RoleListItemResponse
  onEdit: (role: RoleListItemResponse) => void
  onDelete: (role: RoleListItemResponse) => void
}) {
  const { t } = useTranslation('admin-console')
  const canManage = useHasEnablement('Role', 'MANAGE', { global: true })
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  if (!canManage) return null

  return (
    <>
      <IconButton
        size="small"
        aria-label={t('roles.table.actions.menu')}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={anchorEl !== null} onClose={() => setAnchorEl(null)}>
        <MenuItem
          onClick={() => {
            setAnchorEl(null)
            onEdit(role)
          }}
        >
          {t('roles.table.actions.edit')}
        </MenuItem>
        {role.deletable && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null)
              onDelete(role)
            }}
          >
            {t('roles.table.actions.delete')}
          </MenuItem>
        )}
      </Menu>
    </>
  )
}

/**
 * Name over description, as the designs show them: the description is what tells two
 * similarly-named roles apart, so it belongs in the same cell rather than a column of its own.
 */
function RoleRow({
  role,
  showEntities,
  entityTypes,
  onEdit,
  onDelete,
}: {
  role: RoleListItemResponse
  showEntities: boolean
  entityTypes?: ReadonlyMap<number, EntityType>
  onEdit: (role: RoleListItemResponse) => void
  onDelete: (role: RoleListItemResponse) => void
}) {
  const navigate = useNavigate()
  const entities = useRoleEntitiesLabel(role, entityTypes)
  return (
    <TableRow>
      <TableCell>
        {/*
          Both children are laid out as blocks rather than left to their defaults: the name is a
          button-backed Link and the description a caption, which renders a span, so the two
          would otherwise run together on one line.
        */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            sx={{ fontWeight: 600, textAlign: 'left', maxWidth: '100%', ...ellipsis }}
            onClick={() =>
              void navigate({
                to: '/preferences/roles/$roleId',
                params: { roleId: String(role.id) },
                search: { tab: 'access' },
              })
            }
          >
            {role.name}
          </Link>
          {role.description && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', maxWidth: '100%', ...ellipsis }}
            >
              {role.description}
            </Typography>
          )}
        </Box>
      </TableCell>
      {showEntities && <OverflowCell>{entities}</OverflowCell>}
      <TableCell>{role.userCount}</TableCell>
      <TableCell>
        <LastUpdated value={role.lastUpdated} />
      </TableCell>
      <TableCell align="right">
        <RoleActionMenu role={role} onEdit={onEdit} onDelete={onDelete} />
      </TableCell>
    </TableRow>
  )
}

function filterRoles(roles: RoleListItemResponse[], search: string) {
  const term = search.toLowerCase().trim()
  if (!term) return roles
  return roles.filter(
    (role) =>
      role.name.toLowerCase().includes(term) ||
      (role.description?.toLowerCase().includes(term) ?? false),
  )
}

export function RolesTable({ search }: { search: string }) {
  const { t } = useTranslation('admin-console')
  const { data: roles } = useSuspenseQuery(RoleQueries.list())
  const [pendingDelete, setPendingDelete] = useState<RoleListItemResponse | null>(null)
  /**
   * Held here rather than reached through a route, so that editing a role from the grid stays on
   * the grid. The edit route is nested under the role detail page, so navigating to it would
   * open the detail page underneath the drawer as well.
   */
  const [editing, setEditing] = useState<RoleListItemResponse | null>(null)
  const filtered = filterRoles(roles, search)
  /**
   * The Entities column exists only for an organization that scopes roles by Entity Type. The
   * route loads this setting with the list, so the column is settled before the first paint.
   */
  const { data: scopeSettings } = useSuspenseQuery(RoleQueries.scopeSettings())
  const showEntities = scopeSettings.entityTypeScopedAuthorizationEnabled === true
  const entityTypes = useQuery({
    ...RoleQueries.entityTypes(),
    enabled: showEntities,
    select: (types) => new Map(types.map((entityType) => [entityType.id, entityType])),
  })

  return (
    <TableContainer component={Paper} elevation={0} sx={{ width: '100%' }}>
      {showEntities && entityTypes.isLoadingError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void entityTypes.refetch()}>
              {t('roles.retry')}
            </Button>
          }
        >
          {t('roles.entityTypes.loadFailed')}
        </Alert>
      )}
      <Table size="small" sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.50' }}>
            <TableCell>{t('roles.table.columns.role')}</TableCell>
            {showEntities && (
              <TableCell sx={{ width: '25%' }}>{t('roles.table.columns.entityTypes')}</TableCell>
            )}
            <TableCell sx={{ width: '15%' }}>{t('roles.table.columns.users')}</TableCell>
            <TableCell sx={{ width: '25%' }}>{t('roles.table.columns.lastUpdated')}</TableCell>
            <TableCell sx={{ width: 56 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={showEntities ? 5 : 4}
                align="center"
                sx={{ py: 4, color: 'text.secondary' }}
              >
                {t('roles.table.noResults')}
              </TableCell>
            </TableRow>
          ) : (
            filtered.map((role) => (
              <RoleRow
                key={role.id}
                role={role}
                showEntities={showEntities}
                entityTypes={entityTypes.data}
                onEdit={setEditing}
                onDelete={setPendingDelete}
              />
            ))
          )}
        </TableBody>
      </Table>
      <DeleteRoleDialog role={pendingDelete} onClose={() => setPendingDelete(null)} />
      {editing && <RoleForm role={editing} onClose={() => setEditing(null)} />}
    </TableContainer>
  )
}
